import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { ChevronLeft, ChevronRight, GripVertical, Pin, Sparkles } from 'lucide-react';
import { Badge, Button, SimpleTooltip, cn, useShellPanel } from '@command-center/ui-system';

export type AlicePanelState = 'open' | 'collapsed';

const DEFAULT_WIDTH = 480;
const MIN_WIDTH = 380;
const MAX_WIDTH = 720;
/**
 * Space kept for the nav rail plus a workspace column that can still show a
 * record, so the panel never squeezes the page it is assisting into a sliver.
 */
const WORKSPACE_RESERVE = 620;

export function useAlicePanelShell({
  storageKey,
  defaultState = 'open',
}: {
  storageKey: string;
  defaultState?: AlicePanelState;
}) {
  const [state, setStateValue] = useState<AlicePanelState>(() =>
    readState(storageKey, defaultState),
  );
  const preferredWidth = useRef(DEFAULT_WIDTH);
  const [width, setWidthValue] = useState(() => {
    const initial = readWidth(storageKey);
    preferredWidth.current = initial;
    return clampWidth(initial);
  });

  useEffect(() => {
    const fitToViewport = () => setWidthValue(clampWidth(preferredWidth.current));
    fitToViewport();
    window.addEventListener('resize', fitToViewport);
    return () => window.removeEventListener('resize', fitToViewport);
  }, []);

  const setState = useCallback(
    (next: AlicePanelState) => {
      setStateValue(next);
      if (typeof window !== 'undefined') window.localStorage.setItem(`${storageKey}:state`, next);
    },
    [storageKey],
  );

  const setWidth = useCallback(
    (next: number) => {
      const clamped = clampWidth(next);
      setWidthValue(clamped);
      if (clamped !== width) {
        preferredWidth.current = clamped;
        if (typeof window !== 'undefined')
          window.localStorage.setItem(`${storageKey}:width`, String(clamped));
      }
    },
    [storageKey, width],
  );

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = width;
      let nextWidth = width;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const move = (moveEvent: PointerEvent) => {
        nextWidth = clampWidth(startWidth + startX - moveEvent.clientX);
        setWidthValue(nextWidth);
      };
      const cleanup = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', cancel);
        window.removeEventListener('blur', cancel);
      };
      const up = (upEvent: PointerEvent) => {
        nextWidth = clampWidth(startWidth + startX - upEvent.clientX);
        setWidth(nextWidth);
        cleanup();
      };
      const cancel = () => {
        setWidth(nextWidth);
        cleanup();
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', cancel);
      window.addEventListener('blur', cancel);
    },
    [setWidth, width],
  );

  const resizeFromKeyboard = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      setWidth(width + (event.key === 'ArrowLeft' ? 16 : -16));
    },
    [setWidth, width],
  );

  return { state, setState, width, setWidth, startResize, resizeFromKeyboard };
}

export function AlicePanelFrame({
  width,
  onResizeStart,
  onResizeKeyDown,
  resizeLabel = 'Resize Alice panel',
  children,
}: {
  width: number;
  onResizeStart: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onResizeKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  resizeLabel?: string;
  children: ReactNode;
}) {
  // When AppShell has promoted the panel to a full-screen mobile overlay, the
  // shell owns the width and there is nothing to drag-resize against.
  const { inMobileOverlay } = useShellPanel();
  return (
    <div
      className="relative flex h-full min-h-0 w-full flex-col bg-card text-card-foreground"
      style={
        inMobileOverlay
          ? undefined
          : {
              width: `${width}px`,
              maxWidth: `min(${MAX_WIDTH}px, calc(100vw - ${WORKSPACE_RESERVE}px))`,
            }
      }
    >
      {!inMobileOverlay && (
        <div
          role="separator"
          aria-label={resizeLabel}
          aria-orientation="vertical"
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={maxWidthForViewport()}
          aria-valuenow={width}
          aria-valuetext={`${width} pixels`}
          tabIndex={0}
          onPointerDown={onResizeStart}
          onKeyDown={onResizeKeyDown}
          title="Drag to resize. Use the Left and Right arrow keys when focused."
          // Sits entirely inside the panel. Centering it on the divider put
          // half the grab strip — and its col-resize cursor — over the last few
          // pixels of the workspace, which is exactly where the workspace's own
          // scrollbar and right-edge controls live, so they read as unclickable.
          className="group absolute inset-y-0 left-0 z-20 flex w-3 touch-none cursor-col-resize select-none items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="flex h-12 w-2 items-center justify-center rounded-r-md border-y border-r border-border bg-card/95 text-muted-foreground/80 shadow-sm transition-colors group-hover:bg-muted group-hover:text-foreground group-focus-visible:bg-muted group-focus-visible:text-foreground">
            <GripVertical className="h-4 w-3" aria-hidden />
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

/** Public shell name used by portal-specific Alice implementations. */
export const AlicePanelShell = AlicePanelFrame;

export function AliceAvatar({
  avatarPath,
  assistantName,
  compact = false,
}: {
  avatarPath?: string;
  assistantName: string;
  compact?: boolean;
}) {
  return (
    <span
      className={cn(
        'flex flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-emphasis text-emphasis-foreground',
        compact ? 'h-10 max-h-10 w-10 max-w-10' : 'h-12 max-h-12 w-12 max-w-12',
      )}
    >
      {avatarPath ? (
        <img
          src={avatarPath}
          alt={`${assistantName} avatar`}
          className="h-full max-h-full w-full max-w-full object-cover"
        />
      ) : (
        <Sparkles className="h-5 w-5" aria-hidden />
      )}
    </span>
  );
}

export function AliceRail({
  assistantName,
  avatarPath,
  onExpand,
  statusLabel,
  badge,
  quickAccessItems = [],
  quickAccessEmptyLabel = 'No quick access items yet',
  showQuickAccess = true,
  footer,
  className,
}: {
  assistantName: string;
  avatarPath?: string;
  onExpand: () => void;
  statusLabel?: string;
  badge?: ReactNode;
  quickAccessItems?: Array<{
    id: string;
    label: string;
    icon: ReactNode;
    tooltip?: ReactNode;
    onClick?: () => void;
    href?: string;
  }>;
  quickAccessEmptyLabel?: string;
  /** Portals that surface their pins elsewhere opt the strip out entirely. */
  showQuickAccess?: boolean;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-full w-16 flex-col items-center bg-card px-2 py-3 text-muted-foreground',
        className,
      )}
    >
      <SimpleTooltip text={`Open ${assistantName}`}>
        <button
          type="button"
          onClick={onExpand}
          className="group flex min-h-24 w-12 flex-col items-center gap-2 rounded-xl px-1.5 py-2 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Open ${assistantName}`}
        >
          <span className="relative">
            <AliceAvatar avatarPath={avatarPath} assistantName={assistantName} compact />
            <span
              className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card bg-emerald-500"
              aria-label={statusLabel ?? 'Online'}
            />
          </span>
          <ChevronLeft
            className="h-4 w-4 transition-transform group-hover:-translate-x-0.5"
            aria-hidden
          />
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em]">
            {assistantName}
          </span>
        </button>
      </SimpleTooltip>
      {badge}
      {showQuickAccess && <div className="my-3 h-px w-8 bg-border" />}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col items-center gap-2 overflow-y-auto',
          !showQuickAccess && 'hidden',
        )}
        aria-label="Quick access"
      >
        {quickAccessItems.length > 0 ? (
          quickAccessItems.map((item) => (
            <SimpleTooltip key={item.id} text={item.tooltip ?? item.label} side="left">
              {item.href ? (
                <a
                  href={item.href}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-transparent transition-colors hover:border-border hover:bg-muted hover:text-foreground"
                  aria-label={item.label}
                >
                  {item.icon}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={item.onClick}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-transparent transition-colors hover:border-border hover:bg-muted hover:text-foreground"
                  aria-label={item.label}
                >
                  {item.icon}
                </button>
              )}
            </SimpleTooltip>
          ))
        ) : (
          <SimpleTooltip text={quickAccessEmptyLabel}>
            <span
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground/55"
              aria-label={quickAccessEmptyLabel}
            >
              <Pin className="h-4 w-4" aria-hidden />
            </span>
          </SimpleTooltip>
        )}
      </div>
      {statusLabel && <span className="sr-only">{statusLabel}</span>}
      {footer && <span className="mt-auto">{footer}</span>}
    </div>
  );
}

export function AliceHeader({
  assistantName,
  avatarPath,
  subtitle,
  statusLabel,
  badge,
  metaLabel,
  onCollapse,
}: {
  assistantName: string;
  avatarPath?: string;
  subtitle?: string;
  statusLabel?: string;
  badge?: ReactNode;
  metaLabel?: string | null;
  onCollapse: () => void;
}) {
  return (
    <header className="flex items-start gap-3 border-b border-border px-4 py-3">
      <AliceAvatar avatarPath={avatarPath} assistantName={assistantName} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h2 className="truncate text-sm font-semibold">{assistantName}</h2>
          {badge ??
            (statusLabel && (
              <Badge variant="outline" className="text-[10px]">
                {statusLabel}
              </Badge>
            ))}
        </div>
        {subtitle && <p className="mt-0.5 text-[12px] text-muted-foreground">{subtitle}</p>}
        {metaLabel && <p className="mt-0.5 text-[11px] text-muted-foreground">{metaLabel}</p>}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={onCollapse}
        aria-label="Collapse Alice"
      >
        <ChevronRight className="h-4 w-4" aria-hidden />
      </Button>
    </header>
  );
}

function readState(storageKey: string, fallback: AlicePanelState): AlicePanelState {
  if (typeof window === 'undefined') return fallback;
  return window.localStorage.getItem(`${storageKey}:state`) === 'collapsed'
    ? 'collapsed'
    : fallback;
}

function readWidth(storageKey: string): number {
  if (typeof window === 'undefined') return DEFAULT_WIDTH;
  const value = Number(window.localStorage.getItem(`${storageKey}:width`));
  return Number.isFinite(value) && value > 0 ? clampPreferredWidth(value) : DEFAULT_WIDTH;
}

function clampWidth(value: number): number {
  return Math.min(maxWidthForViewport(), clampPreferredWidth(value));
}

function clampPreferredWidth(value: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)));
}

function maxWidthForViewport(): number {
  if (typeof window === 'undefined') return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - WORKSPACE_RESERVE));
}
